/**
 * Master Data Composable
 * マスタデータ管理の状態とロジックを統合
 */

import { ref, computed, reactive } from 'vue'
import { MasterApiService } from '@/services/MasterApiService'

export function useMasterData() {
  // リアクティブな状態
  const state = reactive({
    masterData: [],
    masterTypes: [],
    apiYears: [],
    selectedYear: '',
    selectedMasterType: '',
    isSearched: false,
    loading: false
  })

  // アラート状態
  const alert = reactive({
    message: 'マスタ項目を選択して検索するか、空の状態で検索して全項目を表示してください。',
    class: 'alert-info'
  })

  // 算出プロパティ
  const yearOptions = computed(() => {
    if (!Array.isArray(state.apiYears)) return []
    return state.apiYears.map(year => ({
      value: year.toString(),
      label: year + '年度'
    }))
  })

  const sortedMasterData = computed(() => {
    const order = state.masterTypes.map(opt => opt.value)
    return [...state.masterData].sort((a, b) => {
      return order.indexOf(a._master_type) - order.indexOf(b._master_type)
    })
  })

  // 初期化処理
  const initialize = async () => {
    await Promise.all([
      fetchMasterTypes(),
      fetchYears()
    ])
  }

  // マスタタイプ一覧取得
  const fetchMasterTypes = async () => {
    try {
      const result = await MasterApiService.getMasterTypesList()
      if (result.success) {
        state.masterTypes = result.masterTypes
      } else {
        setAlert(result.message || 'マスタタイプの取得に失敗しました', 'alert-danger')
      }
    } catch (error) {
      console.error('マスタタイプ取得エラー:', error)
      setAlert('マスタタイプの取得に失敗しました', 'alert-danger')
    }
  }

  // 年度一覧取得
  const fetchYears = async () => {
    try {
      const result = await MasterApiService.getYearsList()
      if (result.success) {
        state.apiYears = result.years
      } else {
        console.warn('年度データ取得失敗:', result.message)
      }
    } catch (error) {
      console.error('年度データ取得エラー:', error)
    }
  }

  // マスタデータ検索
  const searchMasterData = async (masterType = null, year = null) => {
    try {
      state.loading = true

      // イベントオブジェクトのハンドリング
      if (masterType && typeof masterType === 'object' && masterType.type) {
        masterType = null
      }
      if (year && typeof year === 'object' && year.type) {
        year = null
      }

      const targetMasterType = masterType || state.selectedMasterType
      const targetYear = year || state.selectedYear

      // マスタタイプの検証
      let masterTypeValue = null
      if (targetMasterType) {
        const masterTypeOption = state.masterTypes.find(type => type.label === targetMasterType)
        masterTypeValue = masterTypeOption?.value

        if (!masterTypeValue) {
          setAlert(`選択されたマスタタイプ "${targetMasterType}" が無効です。`, 'alert-warning')
          return
        }
      }

      // API呼び出し
      const result = await MasterApiService.getMasterList(masterTypeValue, targetYear)

      if (result.success) {
        // レスポンス形式の正規化
        let masterData = []
        if (result.data.master_types) {
          masterData = result.data.master_types
        } else if (result.data.data) {
          masterData = [{
            _group_type: 'master_type',
            _master_type: result.data.master_type || masterTypeValue,
            _title: result.data.master_name || targetMasterType || '',
            items: result.data.data,
            total_count: result.data.total_count || result.data.data.length,
            currentPage: 1,
            itemsPerPage: 50
          }]
        }

        state.masterData = masterData
        state.isSearched = true

        if (masterData.length > 0) {
          setAlert('マスタデータを取得しました', 'alert-success')
        } else {
          setAlert('データが見つかりませんでした。', 'alert-info')
        }
      } else {
        setAlert(result.message, 'alert-danger')
        state.masterData = []
      }
    } catch (error) {
      console.error('マスタデータ検索エラー:', error)
      setAlert(`マスタデータの取得に失敗しました: ${error.message}`, 'alert-danger')
    } finally {
      state.loading = false
    }
  }

  // データクリア
  const clearData = () => {
    state.selectedYear = ''
    state.selectedMasterType = ''
    state.masterData = []
    state.isSearched = false
    setAlert('マスタ項目を選択して検索するか、空の状態で検索して全項目を表示してください。')
  }

  // アラート設定
  const setAlert = (message, className = 'alert-info') => {
    alert.message = message
    alert.class = className
  }

  return {
    // 状態
    state,
    alert,
    
    // 算出プロパティ
    yearOptions,
    sortedMasterData,
    
    // メソッド
    initialize,
    searchMasterData,
    clearData,
    setAlert
  }
}